import React, { useEffect, useMemo, useState } from "react";
import { X, MapPin, Calendar, Clock, Users, Euro, PlusCircle, Car, Repeat } from "lucide-react";
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import AddressField from "@/components/AddressField";
import PublishWearField from "@/components/price/PublishWearField";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Price — Feuille de publication d'un trajet covoitureur.
 * Formulaire mobile-first : départ, arrivée, date, heure, places, prix.
 * Ouvre en overlay full-screen. Appelle POST /api/price/trips.
 */
export default function PublishTripSheet({ open, onClose, onPublished, defaultVehicleType = "car" }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromCoord, setFromCoord] = useState(null);
  const [toCoord, setToCoord] = useState(null);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("18:00");
  const [seats, setSeats] = useState(3);
  const [price, setPrice] = useState(null);
  const [negotiable, setNegotiable] = useState(false);
  const [myPrice, setMyPrice] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [notes, setNotes] = useState("");
  const [vehicleType, setVehicleType] = useState(defaultVehicleType); // car | motorcycle | scooter
  const [busy, setBusy] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [repeatDays, setRepeatDays] = useState([0, 1, 2, 3, 4, 5, 6]);

  // Ré-hydrate le type au changement (profil du covoitureur qui change,
  // ou ouverture du sheet). L'utilisateur peut toujours override.
  React.useEffect(() => {
    if (open) setVehicleType(defaultVehicleType || "car");
  }, [open, defaultVehicleType]);

  const twoWheels = vehicleType === "motorcycle" || vehicleType === "scooter";
  const [longPricing, setLongPricing] = useState(null);
  const [wearCaps, setWearCaps] = useState(null);
  const [wear, setWear] = useState({ enabled: false, amount: "", cv: 5 });

  // Une moto ou un scooter n'accepte qu'un seul passager derrière — on
  // force la valeur "seats" à 1 pour que le formulaire reste cohérent.
  React.useEffect(() => {
    if (twoWheels && seats !== 1) setSeats(1);
  }, [twoWheels, seats]);

  const canSubmit = useMemo(
    () =>
      from.trim().length > 1 &&
      to.trim().length > 1 &&
      fromCoord &&
      toCoord &&
      /^\d{4}-\d{2}-\d{2}$/.test(date) &&
      /^\d{2}:\d{2}$/.test(time) &&
      seats >= 1 &&
      seats <= 4 &&
      price != null &&
      (!repeat || repeatDays.length > 0),
    [from, to, fromCoord, toCoord, date, time, seats, price, repeat, repeatDays]
  );

  const reset = () => {
    setFrom(""); setTo(""); setFromCoord(null); setToCoord(null);
    setDate(today); setTime("18:00"); setSeats(3); setPrice(null); setNotes("");
    setVehicleType(defaultVehicleType || "car");
    setRepeat(false); setRepeatDays([0, 1, 2, 3, 4, 5, 6]);
    setWear({ enabled: false, amount: "", cv: 5 });
  };

  // Prix fixé par Price (même grille que l'instantané), recalculé côté serveur.
  useEffect(() => {
    if (!fromCoord || !toCoord) { setPrice(null); return undefined; }
    let cancel = false;
    setQuoting(true);
    api.get("/price/trips/price-quote", {
      params: { from_lat: fromCoord.lat, from_lng: fromCoord.lng, to_lat: toCoord.lat, to_lng: toCoord.lng, seats },
    })
      .then((r) => { if (!cancel) { setPrice(r.data?.price_per_seat_eur ?? null); setLongPricing(r.data?.long_pricing || null); setWearCaps(r.data?.wear_cap_pp_by_cv || null); setNegotiable(Boolean(r.data?.negotiable)); if (!priceTouched) setMyPrice(r.data?.price_per_seat_eur != null ? String(r.data.price_per_seat_eur) : ""); } })
      .catch(() => { if (!cancel) setPrice(null); })
      .finally(() => { if (!cancel) setQuoting(false); });
    return () => { cancel = true; };
  }, [fromCoord, toCoord, seats]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      toast.error("Renseigne le trajet complet");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/price/trips", {
        from_address: from.trim(),
        from_coord: fromCoord,
        to_address: to.trim(),
        to_coord: toCoord,
        date,
        time,
        seats_total: Number(seats),
        notes: notes.trim(),
        vehicle_type: vehicleType,
        repeat_days: repeat ? repeatDays : null,
        price_per_seat_eur: negotiable ? Number(String(myPrice).replace(",", ".")) || null : null,
        wear_enabled: Boolean(longPricing && !negotiable && !twoWheels && wear.enabled && Number(wear.amount) > 0),
        wear_amount_eur: Number(wear.amount) || 0,
        fiscal_cv: wear.cv,
      });
      toast.success(repeat
        ? (r.data?.created_count
          ? `Trajet récurrent publié (${r.data?.series?.repeat_label}) — ${r.data?.created_count} dates en ligne`
          : `Série enregistrée (${r.data?.series?.repeat_label}) — les dates apparaîtront 14 jours avant`)
        : "Trajet publié 🚀");
      onPublished?.(r.data?.trip);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Erreur lors de la publication");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="publish-trip-sheet"
    >
      <div
        className="relative w-full max-w-md overflow-y-auto bg-white street-border street-shadow"
        style={{
          borderRadius: "24px 24px 0 0",
          marginTop: "auto",
          maxHeight: "92vh",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-5 py-4 street-border-thin"
             style={{ borderTop: 0, borderLeft: 0, borderRight: 0 }}>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground street-border-thin">
              <PlusCircle className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-[Outfit] text-[16px] font-black leading-none">Publier un trajet</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">Covoiturage</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="street-press grid h-9 w-9 place-items-center rounded-full bg-white street-border-thin street-shadow-sm"
            aria-label="Fermer"
            data-testid="publish-trip-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-3">
          <AddressField
            label="Départ"
            icon={<MapPin className="h-4 w-4 text-primary" />}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="Ex. Place Masséna, Nice"
            autoGeoloc
            onPick={(p) => {
              setFrom(p.name || p.place_name || from);
              if (p.lng && p.lat) setFromCoord({ lng: p.lng, lat: p.lat });
            }}
            testid="publish-from"
          />
          <AddressField
            label="Arrivée"
            icon={<MapPin className="h-4 w-4 text-accent" />}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="Ex. Palais des Festivals, Cannes"
            onPick={(p) => {
              setTo(p.name || p.place_name || to);
              if (p.lng && p.lat) setToCoord({ lng: p.lng, lat: p.lat });
            }}
            testid="publish-to"
          />

          {/* Type de véhicule — quand le covoitureur roule seul en moto
              ou scooter, il ne peut prendre qu'un passager derrière. */}
          <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50 mb-2">
              Véhicule utilisé
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "car", label: "Voiture" },
                { v: "motorcycle", label: "Moto" },
                { v: "scooter", label: "Scooter" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setVehicleType(o.v)}
                  data-testid={`publish-vehicle-${o.v}`}
                  className={
                    "street-press flex flex-col items-center gap-1 rounded-xl py-2 street-border-thin " +
                    (vehicleType === o.v ? "bg-primary text-primary-foreground" : "bg-white text-foreground")
                  }
                >
                  {o.v === "car" ? (
                    <Car className="h-5 w-5" strokeWidth={2.5} />
                  ) : (
                    <PriceScooterIcon size={22} />
                  )}
                  <span className="text-[11px] font-black uppercase tracking-widest">{o.label}</span>
                </button>
              ))}
            </div>
            {twoWheels && (
              <p className="mt-2 text-[11px] font-medium text-foreground/60 leading-snug">
                En moto/scooter tu ne peux prendre qu&apos;un seul passager (assis derrière). Casque à la charge du passager.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FieldPill
              icon={<Calendar className="h-4 w-4 text-primary" strokeWidth={2.5} />}
              label={repeat ? "À partir du" : "Date"}
              type="date"
              value={date}
              min={today}
              onChange={(e) => setDate(e.target.value)}
              testid="publish-date"
            />
            <FieldPill
              icon={<Clock className="h-4 w-4 text-accent-foreground" strokeWidth={2.5} />}
              label="Heure"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              testid="publish-time"
            />
          </div>

          <RepeatField repeat={repeat} setRepeat={setRepeat} days={repeatDays} setDays={setRepeatDays} />

          <div className="grid grid-cols-2 gap-3">
            <SeatsField value={seats} onChange={setSeats} testid="publish-seats" disabled={twoWheels} />
            {negotiable ? (
              <label className="block rounded-2xl bg-white p-3 street-border-thin" data-testid="publish-free-price">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Ton prix / place</span>
                <div className="flex items-center">
                  <input inputMode="decimal" value={myPrice} onChange={(e) => { setPriceTouched(true); setMyPrice(e.target.value); }} className="w-full bg-transparent font-[Outfit] text-[22px] font-black outline-none" data-testid="publish-free-price-input" />
                  <span className="font-black">€</span>
                </div>
              </label>
            ) : <PriceField value={price} loading={quoting} testid="publish-price" />}
            {negotiable && price != null && (
              <p className="col-span-2 -mt-1 text-[11px] font-semibold text-foreground/65" data-testid="publish-recommended-price">
                Longue distance : tu fixes ton prix. Prix recommandé par Price : {Number(price).toFixed(2)}€/place. Les passagers peuvent te proposer un autre prix.
              </p>
            )}
            {longPricing && !negotiable && (
              <p className="col-span-2 -mt-1 text-[11px] font-semibold text-foreground/65" data-testid="publish-long-pricing">
                Trajet ≥ 50 km : carburant {longPricing.fuel_eur.toFixed(2)}€ + péages ~{longPricing.toll_eur.toFixed(2)}€, partagés entre {longPricing.divisor} (toi compris).
              </p>
            )}
            {longPricing && !negotiable && !twoWheels && (
              <PublishWearField wear={wear} setWear={setWear} caps={wearCaps} divisor={longPricing.divisor} />
            )}
          </div>

          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
              Note pour les passagers (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex. Petit bagage cabine, pas de fumeur…"
              rows={2}
              maxLength={300}
              className="mt-1 w-full resize-none rounded-2xl bg-secondary/40 p-3 text-[14px] font-medium street-border-thin focus:outline-none"
              data-testid="publish-notes"
            />
          </div>

          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="street-press mt-2 w-full rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="publish-submit"
          >
            {busy ? "Publication…" : "Publier le trajet 🚀"}
          </button>
        </form>
      </div>
    </div>
  );
}

function FieldPill({ icon, label, testid, ...inputProps }) {
  // Le picker natif iOS Safari refuse d'ouvrir si l'input est en
  // `pointer-events-none` + `h-0 w-0`. On met donc l'input EN SUPERPOSITION
  // sur toute la zone cliquable, transparent (opacity:0) mais parfaitement
  // tap-able. iOS/Android reçoivent le tap directement sur l'input →
  // ouverture garantie du picker natif, sans gymnastique `showPicker()`.
  return (
    <div className="street-press relative block rounded-2xl bg-white p-3 street-border-thin street-shadow-sm cursor-pointer select-none" data-testid={`${testid}-pill`}>
      <div className="flex items-center gap-2 pointer-events-none">
        <div className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-primary/10 street-border-thin">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 leading-none">
            {label}
          </div>
          <div className="mt-1 font-[Outfit] text-[14px] font-black leading-tight capitalize truncate">
            {inputProps.value || "—"}
          </div>
        </div>
      </div>
      {/* Input natif en SUPERPOSITION sur toute la pill, transparent.
          Reçoit le tap et ouvre le picker natif iOS/Android nativement.
          `appearance:none` + couleur transparente pour masquer le texte
          natif qui dépasserait sur certaines versions WebKit. */}
      <input
        {...inputProps}
        data-testid={testid}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        style={{
          fontSize: "16px",
          color: "transparent",
          background: "transparent",
          WebkitAppearance: "none",
          appearance: "none",
          border: "none",
          outline: "none",
        }}
      />
    </div>
  );
}

function SeatsField({ value, onChange, testid, disabled = false }) {
  const n = Math.max(1, Math.min(4, Number(value) || 1));
  return (
    <div className={"rounded-2xl bg-white p-3 street-border-thin street-shadow-sm " + (disabled ? "opacity-70" : "")}>
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-accent/25 street-border-thin">
          <Users className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 leading-none">
            Places
          </div>
          <div className="mt-1 flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={() => onChange(Math.max(1, n - 1))}
              disabled={disabled || n <= 1}
              className="street-press grid h-6 w-6 place-items-center rounded-md bg-secondary text-foreground street-border-thin text-[13px] font-black disabled:opacity-40"
              data-testid={`${testid}-dec`}
              aria-label="Une place de moins"
            >−</button>
            <span
              className="font-[Outfit] text-[16px] font-black leading-none tabular-nums"
              data-testid={testid}
            >{n}</span>
            <button
              type="button"
              onClick={() => onChange(Math.min(4, n + 1))}
              disabled={disabled || n >= 4}
              className="street-press grid h-6 w-6 place-items-center rounded-md bg-primary text-primary-foreground street-border-thin text-[13px] font-black disabled:opacity-40"
              data-testid={`${testid}-inc`}
              aria-label="Une place de plus"
            >+</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceField({ value, loading, testid }) {
  return (
    <div className="rounded-2xl bg-secondary/40 p-3 street-border-thin" data-testid={`${testid}-box`}>
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-primary/10 street-border-thin">
          <Euro className="h-4 w-4 text-primary" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 leading-none">
            Prix fixé par Price
          </div>
          <div className="mt-1 font-[Outfit] text-[16px] font-black leading-none tabular-nums" data-testid={testid}>
            {loading ? "…" : value != null ? `${Number(value).toFixed(2).replace(".", ",")} €` : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

const DAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];
const PRESETS = [
  { key: "all", label: "Tous les jours", days: [0, 1, 2, 3, 4, 5, 6] },
  { key: "week", label: "Lun → Ven", days: [0, 1, 2, 3, 4] },
];

function RepeatField({ repeat, setRepeat, days, setDays }) {
  const same = (a) => a.length === days.length && a.every((d) => days.includes(d));
  const toggleDay = (d) => setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort());
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm" data-testid="publish-repeat">
      <button
        type="button"
        onClick={() => setRepeat(!repeat)}
        className="flex w-full items-center justify-between gap-3 text-left"
        data-testid="publish-repeat-toggle"
      >
        <div className="flex items-center gap-2">
          <div className={"grid h-9 w-9 place-items-center rounded-xl street-border-thin " + (repeat ? "bg-primary text-primary-foreground" : "bg-secondary/50")}>
            <Repeat className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-[Outfit] text-[14px] font-black leading-tight">Répéter ce trajet</div>
            <div className="text-[11px] font-medium text-foreground/60">Ex. tous les jours, même heure</div>
          </div>
        </div>
        <span className={"relative h-7 w-12 flex-none rounded-full street-border-thin transition-colors " + (repeat ? "bg-primary" : "bg-secondary/60")}>
          <span className={"absolute top-0.5 h-5 w-5 rounded-full bg-white street-border-thin transition-transform " + (repeat ? "translate-x-[22px]" : "translate-x-0.5")} />
        </span>
      </button>
      {repeat && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setDays(p.days)}
                data-testid={`publish-repeat-${p.key}`}
                className={"street-press rounded-xl py-2 text-[12px] font-black street-border-thin " + (same(p.days) ? "bg-primary text-primary-foreground" : "bg-white")}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex justify-between gap-1">
            {DAY_LETTERS.map((l, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                data-testid={`publish-repeat-day-${i}`}
                className={"street-press grid h-9 w-9 place-items-center rounded-full text-[13px] font-black street-border-thin " + (days.includes(i) ? "bg-accent" : "bg-white text-foreground/50")}
              >
                {l}
              </button>
            ))}
          </div>
          <p className="text-[11px] font-medium leading-snug text-foreground/60">
            Chaque date est publiée automatiquement (14 jours à l&apos;avance) jusqu&apos;à ce que tu arrêtes la série.
          </p>
        </div>
      )}
    </div>
  );
}
